import { useEffect } from "react";
import { VerificationStoreProvider, useVerificationStoreApi, usePaneStore } from "@/features/verification/state/store-context";
import { DocumentCanvas } from "@/features/verification/components/DocumentCanvas";
import { FieldPanel } from "@/features/verification/components/FieldPanel";
import { SnippetPanel } from "@/features/verification/components/SnippetPanel";
import { parseAmountToCents } from "@capture/shared";
import { usePaneDocumentLoad } from "../hooks/usePaneDocumentLoad";
import type { StationDocument } from "../api/client";
import styles from "./RemittanceStation.module.css";

function PaneInner({ doc, amountField, isFocused, onAmountCents, onExhausted }: {
  doc: StationDocument; amountField: string | null; isFocused: boolean;
  onAmountCents: (docId: string, cents: number | null) => void; onExhausted: () => void;
}) {
  const storeApi = useVerificationStoreApi();
  usePaneDocumentLoad(storeApi, doc.id);

  // Live amount → station override: subscribe to the amount field's value.
  const amountValue = usePaneStore((s) => (amountField ? s.fields.find((f) => f.name === amountField)?.value ?? "" : ""));
  useEffect(() => {
    if (amountField) onAmountCents(doc.id, parseAmountToCents(amountValue));
  }, [amountValue, amountField, doc.id, onAmountCents]);

  // When this pane's form is fully keyed, tell the parent to advance.
  const formReady = usePaneStore((s) => s.formReadyToSubmit);
  useEffect(() => { if (formReady) onExhausted(); }, [formReady, onExhausted]);

  return (
    <section className={styles.pane} data-focused={isFocused} aria-label={`Keying ${doc.role ?? "document"}`}>
      <div className={styles.paneCanvas}><DocumentCanvas /></div>
      <div className={styles.paneFields}><FieldPanel /></div>
      <SnippetPanel />
    </section>
  );
}

export function KeyingPane(props: {
  doc: StationDocument; amountField: string | null; isFocused: boolean;
  onFocus: () => void; onAmountCents: (docId: string, cents: number | null) => void; onExhausted: () => void;
}) {
  // No `store` prop — VerificationStoreProvider lazily creates and holds its
  // OWN instance (one per pane, stable across re-renders) when none is given.
  return (
    <div className={styles.paneShell} onFocusCapture={props.onFocus}>
      <VerificationStoreProvider>
        <PaneInner doc={props.doc} amountField={props.amountField} isFocused={props.isFocused} onAmountCents={props.onAmountCents} onExhausted={props.onExhausted} />
      </VerificationStoreProvider>
    </div>
  );
}
